import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect } from "react";
import { Navbar, Footer } from "@/components/site-chrome";

const EASE = [0.16, 1, 0.3, 1] as const;
const FOREST = "#0D4F4A";
const FOREST_DEEP = "#083B3A";
const FOREST_MID = "#2F7E67";
const GOLD = "#D9A441";
const INK = "#101511";
const PAPER = "#F5F0E3";
const MUTED = "#647774";
const FS = "'Montserrat', -apple-system, BlinkMacSystemFont, sans-serif";

const insights = [
  { title: "Dropout risk detected", value: "23 students", detail: "Top factors: attendance decline (62%), fee delay (45%), grade drop (38%). AI recommends counseling sessions for top 10 at-risk students.", color: "#E07B00", confidence: 92 },
  { title: "Route inefficiency found", value: "USD 1.2M/yr", detail: "Bus 07 and Bus 12 have 68% route overlap. Merging saves fuel and 15 min/day. Bus 23 at 94% capacity — consider adding a vehicle.", color: "#0EA5E9", confidence: 88 },
  { title: "Fee collection forecast", value: "USD 45M", detail: "Outstanding across 312 students. 68% are 1-2 terms behind. AI predicts 85% collection rate if payment plans are offered.", color: GOLD, confidence: 85 },
  { title: "Performance trend", value: "+12% vs avg", detail: "Above national average. Mathematics scores declined 5% this term. Recommend teacher training and 3 hours/week remedial classes.", color: FOREST_MID, confidence: 90 },
  { title: "Attendance anomaly", value: "3 flagged", detail: "Students with sudden attendance drop — possible safeguarding concern. Auto-referred to welfare team for follow-up.", color: "#E11D48", confidence: 95 },
  { title: "Cafeteria demand", value: "1,180 meals", detail: "Forecast for lunch based on boarding count, day of week, and weather. Prep kitchen for 15% above normal.", color: "#10B981", confidence: 92 },
];

const risks = [
  { label: "Dropout", value: 23, angle: 0, distance: 0.7, color: "#E07B00", severity: "medium" },
  { label: "Fee Default", value: 45, angle: 60, distance: 0.85, color: GOLD, severity: "high" },
  { label: "Attendance", value: 8, angle: 120, distance: 0.4, color: FOREST_MID, severity: "low" },
  { label: "Safeguarding", value: 3, angle: 180, distance: 0.55, color: "#E11D48", severity: "high" },
  { label: "Transport", value: 12, angle: 240, distance: 0.5, color: "#0EA5E9", severity: "low" },
  { label: "Performance", value: 5, angle: 300, distance: 0.35, color: "#F59E0B", severity: "low" },
];

export function IntelligencePage() {
  return (
    <div className="relative min-h-screen overflow-hidden" style={{ background: FOREST_DEEP, fontFamily: FS }}>
      <div className="pointer-events-none absolute inset-0 opacity-[0.04]" style={{ backgroundImage: `linear-gradient(${FOREST_MID} 1px, transparent 1px), linear-gradient(90deg, ${FOREST_MID} 1px, transparent 1px)`, backgroundSize: "40px 40px" }} />
      <div className="pointer-events-none absolute inset-0" style={{ background: `radial-gradient(ellipse 60% 40% at 30% 10%, ${FOREST_MID}15 0%, transparent 50%), radial-gradient(ellipse 50% 40% at 70% 90%, ${GOLD}08 0%, transparent 50%)` }} />
      <div className="relative z-10">
        <Navbar />
        <IntelligenceHero />
        <RadarSection />
        <InsightCardsSection />
      </div>
      <Footer />
    </div>
  );
}

function IntelligenceHero() {
  return (
    <section className="relative overflow-hidden px-4 pb-12 pt-28 sm:px-8 sm:pt-40 sm:pb-20">
      <div className="relative mx-auto max-w-6xl px-4">
        <div className="mb-3 inline-flex items-center gap-2 rounded-full border px-3 py-1" style={{ borderColor: "rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.04)" }}>
          <span className="h-1.5 w-1.5 rounded-full" style={{ background: GOLD }} />
          <span className="text-[9px] font-bold uppercase tracking-[0.2em]" style={{ color: "rgba(255,255,255,0.7)" }}>Intelligence Layer</span>
        </div>
        <motion.h2 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }} className="text-3xl font-bold tracking-tight sm:text-5xl" style={{ color: "#fff", letterSpacing: "-0.04em", lineHeight: 1.02 }}>
          The school that thinks.
        </motion.h2>
        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.6, ease: EASE }} className="text-3xl font-bold tracking-tight sm:text-5xl" style={{ color: GOLD, letterSpacing: "-0.04em", lineHeight: 1.02, fontStyle: "italic" }}>
          Before you ask.
        </motion.p>
        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.6, ease: EASE }} className="mt-4 max-w-xl text-sm leading-relaxed sm:mt-6 sm:text-lg sm:leading-relaxed" style={{ color: "rgba(255,255,255,0.5)" }}>
          Predictive analytics, AI-driven insights, and automated exception alerts — so leaders act before problems escalate.
        </motion.p>
      </div>
    </section>
  );
}

function RadarSection() {
  const [sweepAngle, setSweepAngle] = useState(0);
  const [hoveredRisk, setHoveredRisk] = useState<number | null>(null);

  useEffect(() => {
    const id = setInterval(() => setSweepAngle(a => (a + 3) % 360), 30);
    return () => clearInterval(id);
  }, []);

  return (
    <section className="relative py-12 sm:py-20">
      <div className="mx-auto max-w-5xl px-4 sm:px-8">
        <div className="grid gap-8 lg:grid-cols-[1fr_1fr] lg:gap-12">
          {/* Radar visualization */}
          <motion.div initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.6, ease: EASE }} className="relative mx-auto flex aspect-square w-full max-w-[380px] items-center justify-center">
            <svg className="h-full w-full" viewBox="-200 -200 400 400">
              {[40, 80, 120, 160].map(r => <circle key={r} cx="0" cy="0" r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="1" />)}
              <line x1="-180" y1="0" x2="180" y2="0" stroke="rgba(255,255,255,0.04)" strokeWidth="1" />
              <line x1="0" y1="-180" x2="0" y2="180" stroke="rgba(255,255,255,0.04)" strokeWidth="1" />
              <defs>
                <linearGradient id="sweepGrad" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor={GOLD} stopOpacity="0" />
                  <stop offset="100%" stopColor={GOLD} stopOpacity="0.15" />
                </linearGradient>
              </defs>
              <g transform={`rotate(${sweepAngle})`}>
                <path d="M 0 0 L 160 0 A 160 160 0 0 0 139 80 Z" fill="url(#sweepGrad)" />
                <line x1="0" y1="0" x2="160" y2="0" stroke={GOLD} strokeWidth="1.5" opacity="0.4" />
              </g>
              {risks.map((r, i) => {
                const rad = (r.angle * Math.PI) / 180;
                const x = 160 * r.distance * Math.cos(rad);
                const y = 160 * r.distance * Math.sin(rad);
                const isHovered = hoveredRisk === i;
                return (
                  <g key={r.label}>
                    {(isHovered || (sweepAngle > r.angle - 15 && sweepAngle < r.angle + 15)) && (
                      <motion.circle cx={x} cy={y} r="20" fill="none" stroke={r.color} strokeWidth="1" animate={{ r: [20, 32, 20], opacity: [0.6, 0, 0.6] }} transition={{ duration: 1.5, repeat: Infinity }} />
                    )}
                    <circle cx={x} cy={y} r={isHovered ? 8 : 6} fill={r.color} style={{ cursor: "pointer", transition: "r 0.2s" }} onMouseEnter={() => setHoveredRisk(i)} onMouseLeave={() => setHoveredRisk(null)} />
                    {isHovered && <text x={x} y={y - 14} textAnchor="middle" fill={r.color} fontSize="11" fontWeight="700">{r.label}: {r.value}</text>}
                  </g>
                );
              })}
            </svg>
          </motion.div>

          {/* Risk summary */}
          <div className="flex flex-col justify-center">
            <h3 className="text-xl font-bold" style={{ color: "#fff", letterSpacing: "-0.02em" }}>Risk Radar · Real-time</h3>
            <p className="mt-1 text-sm" style={{ color: "rgba(255,255,255,0.5)" }}>AI continuously monitors six risk dimensions across your institution.</p>
            <div className="mt-6 space-y-2">
              {risks.map((r, i) => (
                <motion.button key={r.label} onClick={() => setHoveredRisk(hoveredRisk === i ? null : i)} onMouseEnter={() => setHoveredRisk(i)} onMouseLeave={() => setHoveredRisk(null)} className="flex w-full items-center justify-between rounded-xl border p-3 text-left transition-all" style={{ borderColor: hoveredRisk === i ? `${r.color}40` : "rgba(255,255,255,0.06)", background: hoveredRisk === i ? `${r.color}10` : "rgba(255,255,255,0.02)" }}>
                  <div className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ background: r.color }} />
                    <span className="text-sm font-medium" style={{ color: "#fff" }}>{r.label}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-bold tabular-nums" style={{ color: r.color }}>{r.value}</span>
                    <span className="rounded-full px-2 py-0.5 text-[9px] font-bold uppercase" style={{ background: r.severity === "high" ? "#E11D4820" : r.severity === "medium" ? "#E07B0020" : "#2F7E6720", color: r.severity === "high" ? "#E11D48" : r.severity === "medium" ? "#E07B00" : FOREST_MID }}>{r.severity}</span>
                  </div>
                </motion.button>
              ))}
            </div>
            <div className="mt-4 grid grid-cols-4 gap-2">
              {[{ label: "High Risk", value: 2, color: "#E11D48" }, { label: "Medium", value: 1, color: "#E07B00" }, { label: "Low Risk", value: 3, color: FOREST_MID }, { label: "Safe", value: 187, color: "#10B981" }].map(s => (
                <div key={s.label} className="rounded-xl border p-3 text-center" style={{ borderColor: "rgba(255,255,255,0.06)", background: "rgba(255,255,255,0.02)" }}>
                  <p className="text-lg font-bold tabular-nums" style={{ color: s.color }}>{s.value}</p>
                  <p className="text-[8px] uppercase tracking-wider" style={{ color: "rgba(255,255,255,0.4)" }}>{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function InsightCardsSection() {
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setActiveIndex(i => (i + 1) % insights.length), 3000);
    return () => clearInterval(id);
  }, []);

  return (
    <section className="relative py-12 sm:py-20" style={{ background: PAPER }}>
      <div className="mx-auto max-w-5xl px-4 sm:px-8">
        <div className="mb-3 inline-flex items-center gap-2 rounded-full border px-3 py-1" style={{ borderColor: `${FOREST}20`, background: `${FOREST}06` }}>
          <span className="h-1.5 w-1.5 rounded-full" style={{ background: GOLD }} />
          <span className="text-[9px] font-bold uppercase tracking-[0.2em]" style={{ color: FOREST }}>AI Predictions</span>
        </div>
        <h2 className="text-2xl font-bold tracking-tight sm:text-4xl" style={{ color: INK, letterSpacing: "-0.04em", lineHeight: 1.05 }}>
          Insights that <span style={{ color: FOREST, fontStyle: "italic" }}>act themselves.</span>
        </h2>

        <AnimatePresence mode="wait">
          <motion.div key={activeIndex} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.3, ease: EASE }} className="mt-8 rounded-2xl border p-6" style={{ borderColor: `${insights[activeIndex].color}25`, background: `${insights[activeIndex].color}06` }}>
            <div className="flex items-center gap-3">
              <div className="grid h-12 w-12 place-items-center rounded-xl" style={{ background: `${insights[activeIndex].color}15` }}>
                <svg viewBox="0 0 24 24" width={24} height={24} fill="none" stroke={insights[activeIndex].color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" /></svg>
              </div>
              <div>
                <p className="text-lg font-bold" style={{ color: INK }}>{insights[activeIndex].title}</p>
                <p className="text-2xl font-bold tabular-nums" style={{ color: insights[activeIndex].color }}>{insights[activeIndex].value}</p>
              </div>
              <div className="ml-auto text-right">
                <p className="text-xs font-bold" style={{ color: insights[activeIndex].color }}>{insights[activeIndex].confidence}%</p>
                <p className="text-[9px] uppercase tracking-wider" style={{ color: MUTED }}>confidence</p>
              </div>
            </div>
            <p className="mt-4 text-sm leading-relaxed" style={{ color: MUTED }}>{insights[activeIndex].detail}</p>
            <div className="mt-3 h-1.5 overflow-hidden rounded-full" style={{ background: `${insights[activeIndex].color}10` }}>
              <motion.div className="h-full rounded-full" style={{ background: insights[activeIndex].color }} animate={{ width: `${insights[activeIndex].confidence}%` }} transition={{ duration: 0.5 }} />
            </div>
          </motion.div>
        </AnimatePresence>

        <div className="mt-4 flex items-center justify-center gap-2">
          {insights.map((ins, i) => (
            <button key={i} onClick={() => setActiveIndex(i)} className="h-2 rounded-full transition-all" style={{ background: activeIndex === i ? ins.color : "#D5DED4", width: activeIndex === i ? 24 : 8 }} aria-label={ins.title} />
          ))}
        </div>

        <div className="mt-8 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {insights.map((ins, i) => (
            <motion.button key={ins.title} onClick={() => setActiveIndex(i)} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }} className="rounded-xl border p-4 text-left transition-all" style={{ borderColor: activeIndex === i ? `${ins.color}40` : "#D5DED4", background: activeIndex === i ? `${ins.color}08` : "#fff" }}>
              <p className="text-xs font-bold" style={{ color: ins.color }}>{ins.title}</p>
              <p className="mt-1 text-lg font-bold tabular-nums" style={{ color: INK }}>{ins.value}</p>
              <p className="mt-0.5 text-[10px]" style={{ color: MUTED }}>{ins.confidence}% confidence</p>
            </motion.button>
          ))}
        </div>
      </div>
    </section>
  );
}

export default IntelligencePage;
